import { useLatestRef } from "../../utils/use-latest-ref.js";
import { useDestroy } from "../../utils/use-destroy.js";
import { useSafeId } from "../../utils/use-safe-id.js";
import { DialogCore, DialogDataAttrs } from "@videojs/core";
import { createDialog, createTransition } from "@videojs/core/dom";
import { useSnapshot } from "@videojs/store/react";
import { useEffect, useLayoutEffect, useState } from "react";
//#region src/ui/dialog/use-dialog-root.ts
function useDialogRoot({ open: controlledOpen, defaultOpen = DialogCore.defaultProps.defaultOpen, closeOnEscape = DialogCore.defaultProps.closeOnEscape, onOpenChange: onOpenChangeProp, onOpenChangeComplete: onOpenChangeCompleteProp, coreFactory = createDialogCore, stateAttrMap = DialogDataAttrs, idPrefix = "dialog", interactionRoot }) {
	const [core] = useState(coreFactory);
	core.setProps({
		open: controlledOpen,
		defaultOpen,
		closeOnEscape
	});
	const isControlled = controlledOpen !== void 0;
	const onOpenChangeRef = useLatestRef(onOpenChangeProp);
	const onOpenChangeCompleteRef = useLatestRef(onOpenChangeCompleteProp);
	const closeOnEscapeRef = useLatestRef(closeOnEscape);
	const [dialog] = useState(() => {
		const instance = createDialog({
			transition: createTransition(),
			onOpenChange: (nextOpen) => onOpenChangeRef.current?.(nextOpen),
			onOpenChangeComplete: (nextOpen) => onOpenChangeCompleteRef.current?.(nextOpen),
			closeOnEscape: () => closeOnEscapeRef.current
		});
		if (!isControlled && defaultOpen) instance.open();
		return instance;
	});
	const popupId = useSafeId(`${idPrefix}-popup`);
	const titleId = useSafeId(`${idPrefix}-title`);
	const descriptionId = useSafeId(`${idPrefix}-desc`);
	core.setTitleId(titleId);
	core.setDescriptionId(descriptionId);
	useEffect(() => {
		if (controlledOpen === void 0) return;
		const { active: inputOpen } = dialog.input.current;
		if (controlledOpen === inputOpen) return;
		if (controlledOpen) dialog.open();
		else dialog.close();
	}, [controlledOpen, dialog]);
	useLayoutEffect(() => {
		dialog.setInteractionRoot(interactionRoot ?? null);
	}, [dialog, interactionRoot]);
	useDestroy(dialog);
	const input = useSnapshot(dialog.input);
	const modality = useSnapshot(dialog.modality);
	core.setInput(input);
	core.setDocumentModal(modality.documentModal);
	return {
		core,
		dialog,
		state: core.getState(),
		stateAttrMap,
		popupId
	};
}
function createDialogCore() {
	return new DialogCore();
}
//#endregion
export { useDialogRoot };

//# sourceMappingURL=use-dialog-root.js.map